import type { MouseEventHandler, ReactNode } from "react"
import { Info } from "lucide-react"
import { cn } from "@lib/utils/helpers"
import { InfoPopover, type InfoPopoverAlign } from "@/components/ui/InfoPopover"

export interface SectionInfoIconProps {
    content: ReactNode
    "data-testid"?: string
    /** Default: "More information" */
    "aria-label"?: string
    className?: string
    /** Lucide icon size; default 14. */
    iconSize?: number
    /** Max popover width in CSS pixels (default 640); clamped to viewport. */
    maxWidth?: number
    /** Horizontal alignment relative to the trigger. Default `center`. */
    align?: InfoPopoverAlign
    /** Hint for table last row — popover prefers opening above. */
    isLastRow?: boolean
    /** When true (default), caps panel height and scrolls on the popover shell; avoid a second inner overflow. */
    clampPanelHeight?: boolean
    /** Merged with default inner content styles (typography, padding, optional scroll). */
    contentClassName?: string
    onClick?: MouseEventHandler<HTMLButtonElement>
}

/**
 * Green (i) next to a label showing help text in a popover, rendered through a
 * portal so it appears above modals, drawers, and overflow-hidden ancestors.
 */
export function SectionInfoIcon({
    content,
    "data-testid": dataTestId,
    "aria-label": ariaLabel = "More information",
    className,
    iconSize = 14,
    maxWidth = 640,
    align = "center",
    isLastRow = false,
    clampPanelHeight = true,
    contentClassName,
    onClick,
}: SectionInfoIconProps) {
    return (
        <InfoPopover
            content={content}
            align={align}
            isLastRow={isLastRow}
            maxWidth={maxWidth}
            hideDelayMs={200}
            className="!p-0 shadow-md"
            clampPanelHeight={clampPanelHeight}
            closeOnClick={Boolean(onClick)}
            contentClassName={cn(
                "px-4 py-2.5 text-left text-xs font-normal leading-relaxed text-slate-800 sm:px-5 sm:py-3 sm:text-sm",
                contentClassName,
            )}
            testId={dataTestId ? `${dataTestId}-tooltip` : undefined}
        >
            <button
                type="button"
                data-testid={dataTestId}
                tabIndex={0}
                className={cn(
                    "inline-flex h-[1em] w-[1em] items-center justify-center align-middle cursor-pointer rounded border-0 bg-transparent p-0 text-green-600 hover:text-green-700 focus-visible:outline focus-visible:ring-2 focus-visible:ring-green-500/40",
                    className,
                )}
                aria-label={ariaLabel}
                onClick={(event) => {
                    event.currentTarget.blur()
                    onClick?.(event)
                }}
            >
                <Info
                    size={iconSize}
                    className="block shrink-0"
                    strokeWidth={2.25}
                    aria-hidden
                />
            </button>
        </InfoPopover>
    )
}
